import { Button, Divider, Form, Select, Typography } from "antd";
import type { FormInstance } from "antd";
import React from "react";
import { translate } from "shared/languages";
import { CustomerPackage, CustomerTable } from "shared/types";
import { colors } from "shared/theme/color";
import {
  personFilterOption,
  renderPersonOption,
} from "shared/components/PersonSelectOption";
import { SectionEyebrow } from "./SectionEyebrow";
import { PackageRadioList } from "./PackageRadioList";
import { textPx } from "shared/constants/zoom";

type CustomerPackagesByCustomer = Record<string, CustomerPackage[]>;

interface ClientPackageSectionProps {
  form: FormInstance;
  customers: CustomerTable[] | undefined;
  customerLoading: boolean;
  customerPackages: CustomerPackagesByCustomer | undefined;
  customerPackageLoading: boolean;
  selectedCustomerIds: string[];
  numberOfCustomers: number;
  isPrivateClass: boolean;
  onCustomerChange: () => void;
  // Navigates to the client's detail page to view/add packages &
  // memberships — the drawer's in-progress state is preserved and restored
  // on return (see CreateScheduleDrawer.tsx's handleBuyPackage).
  onBuyPackage: (customerId: string) => void;
}

export const ClientPackageSection = ({
  form,
  customers,
  customerLoading,
  customerPackages,
  customerPackageLoading,
  selectedCustomerIds,
  numberOfCustomers,
  isPrivateClass,
  onCustomerChange,
  onBuyPackage,
}: ClientPackageSectionProps) => {
  // No class picked yet → render one disabled placeholder slot so the user
  // sees the section's structure before selecting a class.
  const slotCount = isPrivateClass ? numberOfCustomers : 1;
  const fieldsDisabled = !isPrivateClass;

  const resolveCustomerId = (index: number): string | undefined => {
    const option = form.getFieldValue(`customer_id_${index}`);
    return option && (option.value ?? option);
  };

  return (
    <div style={{ marginBottom: 8 }}>
      <SectionEyebrow
        num={3}
        label={translate("schedule.drawer.client_package")}
      />
      {Array.from({ length: slotCount }).map((_, index) => {
        const custOption = form.getFieldValue(`customer_id_${index}`);
        const custId = custOption && (custOption.value ?? custOption);
        const custPackages =
          (customerPackages && customerPackages[custId]) || [];

        return (
          <React.Fragment key={index}>
            {index > 0 && <Divider style={{ margin: "16px 0" }} />}
            {slotCount > 1 && (
              <Typography.Text
                style={{
                  fontSize: textPx(12),
                  fontWeight: 600,
                  color: colors.ink2,
                  marginBottom: 6,
                  display: "block",
                }}
              >
                {`${translate("customer")} ${index + 1}`}
              </Typography.Text>
            )}
            <Form.Item
              name={`customer_id_${index}`}
              label={translate("customer")}
              rules={[
                {
                  required: true,
                  message: `${translate("form.error.select")} ${translate(
                    "customer"
                  )}`,
                },
              ]}
              style={{ marginBottom: 12 }}
            >
              <Select
                disabled={fieldsDisabled}
                labelInValue
                showSearch
                filterOption={personFilterOption}
                optionRender={renderPersonOption}
                placeholder={`${translate(
                  "form.placeholders.select"
                )} ${translate("customer")}`}
                loading={customerLoading}
                onChange={() => {
                  form.setFieldValue(`customer_package_id_${index}`, undefined);
                  onCustomerChange();
                }}
                options={customers
                  ?.filter(
                    (c) =>
                      c.id === custId || !selectedCustomerIds.includes(c.id)
                  )
                  .map((c) => ({
                    label: c.full_name,
                    value: c.id,
                    phone: c.phone_number,
                  }))}
              />
            </Form.Item>
            <Form.Item
              name={`customer_package_id_${index}`}
              label={translate("label.customer_package")}
              rules={[
                {
                  required: true,
                  message: `${translate("form.error.select")} ${translate(
                    "label.customer_package"
                  )}`,
                },
              ]}
              style={{ marginBottom: 0 }}
            >
              {/* Selectable package cards (prototype list-item pattern).
                  No valid package for this class → quiet inline notice with a
                  secondary action rendered in the control slot, snug under
                  the label; the required rule still blocks submission. */}
              <PackageRadioList
                packages={custPackages}
                emptyNotice={
                  custOption && !customerPackageLoading ? (
                    <div
                      style={{
                        padding: "12px 14px",
                        borderRadius: 12,
                        background: colors.bgSoft,
                        border: `1px solid ${colors.line}`,
                      }}
                    >
                      <Typography.Text
                        style={{
                          display: "block",
                          fontSize: textPx(13),
                          color: colors.ink2,
                          marginBottom: 10,
                        }}
                      >
                        {translate("schedule.drawer.no_valid_package")}
                      </Typography.Text>
                      <Button
                        size="small"
                        onClick={() =>
                          onBuyPackage(resolveCustomerId(index) ?? "")
                        }
                      >
                        {translate("schedule.drawer.manage_client_packages")}
                      </Button>
                    </div>
                  ) : undefined
                }
              />
            </Form.Item>
          </React.Fragment>
        );
      })}
    </div>
  );
};
